'use client';

import { useEffect } from 'react';
import Link from 'next/link';

export default function PublicError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Public page error:', error);
  }, [error]);

  const isDev = process.env.NODE_ENV === 'development';

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="text-center max-w-2xl">
        {/* Branding */}
        <div className="mb-8">
          <Link
            href="/"
            className="inline-block text-3xl font-bold text-keiro-green-dark hover:text-keiro-green transition-colors"
          >
            Keiront
          </Link>
        </div>

        {/* Error Icon */}
        <div className="mb-6">
          <div className="text-6xl sm:text-7xl mb-4">⚠️</div>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-keiro-green-dark mb-4">
          Something went wrong
        </h1>

        {/* Description */}
        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
          We encountered an unexpected error. Please try again or go back to the home page.
        </p>

        {/* Dev Error Details (only in development) */}
        {isDev && error?.message && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
            <p className="text-sm font-mono text-red-800 break-words">
              <strong>Error:</strong> {error.message}
            </p>
            {error.digest && (
              <p className="text-xs text-red-600 mt-2">
                <strong>Digest:</strong> {error.digest}
              </p>
            )}
          </div>
        )}

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={reset}
            className="px-8 py-3 bg-keiro-green text-white font-semibold rounded-lg hover:bg-keiro-green-dark transition-colors shadow-sm"
          >
            Try Again
          </button>
          <Link
            href="/"
            className="px-8 py-3 border-2 border-keiro-green/30 text-keiro-green-dark font-semibold rounded-lg hover:bg-keiro-mist transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
